import { gql, useMutation } from '@apollo/client';
import { Alert, Button, FormControl, FormLabel } from '@apollo/orbit';
import { getIntrospectionQuery } from 'graphql';
import React, { useCallback, useState } from 'react';

import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { PermissionWarning } from 'src/components/permissionGuards/permissionWarning/PermissionWarning';
import { URLField } from 'src/components/urlField/URLField';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { PublishModeTab } from '../shared';

const UPLOAD_SCHEMA = gql`
  mutation UploadSchemaIntrospectionMutation(
    $graphVariant: String!
    $graphId: ID!
    $uploadSchemaSchema: IntrospectionSchemaInput
  ) {
    service(id: $graphId) {
      uploadSchema(tag: $graphVariant, schema: $uploadSchemaSchema) {
        success
      }
    }
  }
`;

const UPDATE_URL = gql`
  mutation UpdateVariantUrlMutation(
    $graphVariant: String!
    $graphId: ID!
    $url: String
  ) {
    service(id: $graphId) {
      variant(name: $graphVariant) {
        updateURL(url: $url) {
          id
          url
        }
      }
    }
  }
`;

export const EndpointTab = ({
  firstPublish,
  graphId,
  graphVariant,
  onComplete,
  setTab,
}: {
  firstPublish: boolean;
  graphId: string;
  graphVariant: string;
  onComplete?: () => void;
  setTab: (tab: PublishModeTab) => void;
}) => {
  const [uploadSchema, { loading: uploadLoading }] = useMutation<
    GraphQLTypes.UploadSchemaIntrospectionMutation,
    GraphQLTypes.UploadSchemaIntrospectionMutationVariables
  >(UPLOAD_SCHEMA);
  const [updateUrl, { client, loading: updateLoading }] = useMutation<
    GraphQLTypes.UpdateVariantUrlMutation,
    GraphQLTypes.UpdateVariantUrlMutationVariables
  >(UPDATE_URL, {
    onCompleted() {
      client.cache.evict({
        id: client.cache.identify({ __typename: 'Service', id: graphId }),
      });
      client.cache.gc();
      onComplete?.();
    },
  });
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState('http://localhost:4000');
  const [error, setError] = useState('');

  const handleOnChangeEndpointUrl = useCallback(
    (href: string) => setUrl(href),
    [setUrl],
  );

  return (
    <>
      {firstPublish ? (
        <p className="mb-2">
          Pick this option to do a <span className="italic">one-time</span>{' '}
          introspection to get started quickly.
        </p>
      ) : (
        <p className="mb-2">
          Do a <span className="italic">one-time</span> introspection.
        </p>
      )}
      {error && (
        <Alert status="error" className="mb-2">
          {error}
        </Alert>
      )}
      <FormControl className="mb-4">
        <FormLabel>Endpoint URL</FormLabel>
        <URLField
          isSocket={false}
          autoFocus
          placeholder="http://localhost:4000"
          value={url}
          onChange={handleOnChangeEndpointUrl}
        />
      </FormControl>

      <Alert status="info">
        To keep things current automatically, follow the instructions in the{' '}
        <ClickableText
          className="font-semibold underline"
          onClick={() => {
            setTab(PublishModeTab.CI);
          }}
        >
          CI/CD section
        </ClickableText>{' '}
        or use the{' '}
        <ClickableText
          className="font-semibold underline"
          onClick={() => {
            setTab(PublishModeTab.SchemaReporting);
          }}
        >
          Apollo Server integration
        </ClickableText>
        .
      </Alert>

      <div className="mt-8 flex items-end justify-end">
        <div className="flex-1" />
        <PermissionWarning
          graphId={graphId}
          graphVariant={graphVariant}
          graphVariantPermissions="canPushSchemas"
          childRenderer={(hasPermission) => (
            <Button
              type="submit"
              isLoading={loading || uploadLoading || updateLoading}
              loadingText="Loading"
              isDisabled={!url || !hasPermission}
              onClick={async (event) => {
                event.preventDefault();

                setLoading(true);
                try {
                  const response = await fetch(url, {
                    method: 'POST',
                    body: JSON.stringify({ query: getIntrospectionQuery() }),
                    headers: { 'Content-Type': 'application/json' },
                  });
                  const result = await response.text();

                  if (response.ok) {
                    // Success! Got the introspection result.
                    await uploadSchema({
                      variables: {
                        graphVariant,
                        graphId,
                        uploadSchemaSchema: (
                          JSON.parse(result) as {
                            data: {
                              __schema: GraphQLTypes.UploadSchemaIntrospectionMutationVariables['uploadSchemaSchema'];
                            };
                          }
                        ).data.__schema,
                      },
                    });
                    await updateUrl({
                      variables: { graphVariant, graphId, url },
                    });
                  } else {
                    setError(result);
                  }
                } catch (e) {
                  setError((e as Error).message);
                }
                setLoading(false);
              }}
            >
              Introspect and Upload
            </Button>
          )}
        />
      </div>
    </>
  );
};
